import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import {
  AdjustmentsHorizontalIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { NativeSelect } from "@/components/ui/NativeSelect";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import {
  getContractCampaigns,
  isOnCallContract,
  listMaintenanceContracts,
  maintenanceCampaignProgress,
  WARTUNGEN_REALTIME_DOMAINS,
  type MaintenanceCampaign,
  type MaintenanceContract,
} from "@/lib/wartungen";
import { useCurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import { useRealtimeSync } from "@/lib/useRealtime";
import { MaintenanceCampaignCard } from "./components/MaintenanceCampaignCard";
import { ChipRow, KpiTile } from "./components/OverviewPrimitives";
import { VertraegeTab } from "./components/VertraegeTab";
import { buildCampaignView, dedupeOptions, FILTER_ALL, type CampaignView } from "./lib/campaignView";
import { useCampaignAverageTimes } from "./lib/useCampaignAverageTimes";
import { NeueWartungDialog } from "./NeueWartungDialog";

type Tab = "wartungen" | "vertraege" | "auf-zuruf";
type ProgressFilter = "all" | "open" | "in_progress" | "done";

/** Fortschrittszustand einer Wartung; ohne Objekte gilt sie als „offen“. */
function campaignProgressState(view: CampaignView): Exclude<ProgressFilter, "all"> {
  const progress = maintenanceCampaignProgress(view.campaign);
  if (!progress) return "open";
  if (progress.completed >= progress.total) return "done";
  if (progress.completed > 0) return "in_progress";
  return "open";
}

function campaignMatchesQuery(view: CampaignView, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    view.title,
    view.customer.label,
    view.location.label,
    view.category.label,
    view.contractLabel,
    view.year,
    view.scheduleLabel,
    view.workflow.label,
  ]
    .join(" ")
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
}

function OnboardingSteps({
  hasContracts,
  onCreateContract,
  onCreateMaintenance,
}: {
  hasContracts: boolean;
  onCreateContract: () => void;
  onCreateMaintenance: () => void;
}) {
  const steps = [
    {
      icon: <DocumentPlusIcon className="h-5 w-5" aria-hidden />,
      title: "Wartungsvertrag anlegen",
      text: "Kunde, Orte und Wartungskategorien hinterlegen. Der Vertrag ist die Grundlage für alle Wartungen.",
      action: (
        <Button type="button" variant={hasContracts ? "secondary" : "primary"} size="sm" onClick={onCreateContract}>
          {hasContracts ? "Weiteren Vertrag anlegen" : "Vertrag anlegen"}
        </Button>
      ),
      done: hasContracts,
    },
    {
      icon: <PlusIcon className="h-5 w-5" aria-hidden />,
      title: "Wartung anlegen",
      text: "Vertrag, Projekt und Zeitraum wählen – daraus entsteht eine Wartung mit eigener Objektliste.",
      action: (
        <Button type="button" variant={hasContracts ? "primary" : "secondary"} size="sm" disabled={!hasContracts} onClick={onCreateMaintenance}>
          Wartung anlegen
        </Button>
      ),
      done: false,
    },
    {
      icon: <ClipboardDocumentCheckIcon className="h-5 w-5" aria-hidden />,
      title: "Objekte erfassen & Wartung durchführen",
      text: "In der Wartung Objekte anlegen, optional auf Plänen platzieren und je Objekt das Wartungsformular ausfüllen.",
      action: null,
      done: false,
    },
  ];
  return (
    <section className="rounded-ios border border-dashed border-separator bg-card p-5 shadow-ios">
      <h2 className="text-base font-semibold text-label">So funktionieren Wartungen</h2>
      <p className="mt-1 text-sm text-label-secondary">
        In drei Schritten von Vertrag zur durchgeführten Wartung.
      </p>
      <ol className="mt-4 grid gap-3 md:grid-cols-3">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className={clsx(
              "flex flex-col rounded-xl border p-4",
              step.done ? "border-status-green/40 bg-status-green/5" : "border-separator bg-card-elevated/40",
            )}
          >
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  step.done ? "bg-status-green text-label-on-accent" : "bg-accent/10 text-accent",
                )}
              >
                {step.done ? "✓" : step.icon}
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Schritt {index + 1}</span>
            </div>
            <h3 className="mt-2 text-sm font-semibold text-label">{step.title}</h3>
            <p className="mt-1 flex-1 text-sm text-label-secondary">{step.text}</p>
            {step.action ? <div className="mt-3">{step.action}</div> : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function WartungenIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const showLegacyAccess = userHasGroup(user, "App-Admins");
  const isBuero = userIsBuero(user);

  const tabParam = searchParams.get("tab");
  const tab: Tab = tabParam === "vertraege" || tabParam === "auf-zuruf" ? tabParam : "wartungen";
  const setTab = useCallback(
    (next: Tab) => {
      const usp = new URLSearchParams(searchParams);
      if (next === "wartungen") usp.delete("tab");
      else usp.set("tab", next);
      setSearchParams(usp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [contracts, setContracts] = useState<MaintenanceContract[]>([]);
  const [views, setViews] = useState<CampaignView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wizardContract, setWizardContract] = useState<MaintenanceContract | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Filter „Wartungen“
  const currentYear = String(new Date().getFullYear());
  const [query, setQuery] = useState("");
  const [year, setYear] = useState<string>(currentYear);
  const [category, setCategory] = useState<string>(FILTER_ALL);
  const [location, setLocation] = useState<string>(FILTER_ALL);
  const [customer, setCustomer] = useState<string>(FILTER_ALL);
  const [progressFilter, setProgressFilter] = useState<ProgressFilter>("all");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const loadedContracts = await listMaintenanceContracts({ limit: 500 });
      const validContracts = loadedContracts.filter((c) => Number(c.id) > 0);
      const results = await Promise.all(
        validContracts.map(async (c) => {
          try {
            return [Number(c.id), await getContractCampaigns(Number(c.id))] as const;
          } catch {
            return [Number(c.id), [] as MaintenanceCampaign[]] as const;
          }
        }),
      );
      const byContract = new Map(results);
      const built: CampaignView[] = [];
      for (const contract of validContracts) {
        const cid = Number(contract.id);
        (byContract.get(cid) || []).forEach((campaign, index) => {
          built.push(buildCampaignView(campaign, contract, built.length + index));
        });
      }
      built.sort((a, b) => {
        if (a.year !== b.year) return b.year.localeCompare(a.year, "de", { numeric: true });
        const cc = a.customer.label.localeCompare(b.customer.label, "de", { sensitivity: "base" });
        if (cc !== 0) return cc;
        return a.title.localeCompare(b.title, "de");
      });
      setContracts(validContracts);
      setViews(built);
    } catch {
      setError("Wartungen konnten nicht geladen werden.");
      setContracts([]);
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: [...WARTUNGEN_REALTIME_DOMAINS],
    onChange: () => void load({ silent: true }),
  });

  const averageByCampaign = useCampaignAverageTimes(views, isBuero);

  /* ───────────── Wartungen: Optionen & Filterung ───────────── */

  const yearOptions = useMemo(() => {
    const set = new Set(views.map((v) => v.year).filter((y) => y && y !== "–"));
    if (!set.has(currentYear) && views.length) set.add(currentYear);
    return Array.from(set)
      .sort((a, b) => b.localeCompare(a, "de", { numeric: true }))
      .map((y) => ({ key: y, label: y, count: views.filter((v) => v.year === y).length }));
  }, [views, currentYear]);

  // Jahr automatisch auf das jüngste verfügbare Jahr setzen, falls im aktuellen nichts existiert.
  useEffect(() => {
    if (loading || !views.length) return;
    if (year !== FILTER_ALL && !views.some((v) => v.year === year)) {
      const newest = yearOptions.find((o) => o.count > 0);
      if (newest) setYear(newest.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, views.length]);

  const yearScoped = useMemo(
    () => views.filter((v) => year === FILTER_ALL || v.year === year),
    [views, year],
  );
  const categoryOptions = useMemo(
    () => dedupeOptions(yearScoped.map((v) => v.category).filter((c) => c.key !== FILTER_ALL)),
    [yearScoped],
  );
  const locationOptions = useMemo(() => dedupeOptions(yearScoped.map((v) => v.location)), [yearScoped]);
  const customerOptions = useMemo(() => dedupeOptions(yearScoped.map((v) => v.customer)), [yearScoped]);

  const kpis = useMemo(() => {
    const counts = { total: yearScoped.length, open: 0, in_progress: 0, done: 0 };
    for (const v of yearScoped) counts[campaignProgressState(v)] += 1;
    return counts;
  }, [yearScoped]);

  const filtered = useMemo(
    () =>
      yearScoped.filter(
        (v) =>
          (category === FILTER_ALL || v.category.key === category) &&
          (location === FILTER_ALL || v.location.key === location) &&
          (customer === FILTER_ALL || v.customer.key === customer) &&
          (progressFilter === "all" || campaignProgressState(v) === progressFilter) &&
          campaignMatchesQuery(v, query),
      ),
    [yearScoped, category, location, customer, progressFilter, query],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: CampaignView[] }>();
    for (const v of filtered) {
      const entry = map.get(v.customer.key) ?? { label: v.customer.label, items: [] };
      entry.items.push(v);
      map.set(v.customer.key, entry);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "de", { sensitivity: "base" }),
    );
  }, [filtered]);

  const activeFilterCount =
    (category !== FILTER_ALL ? 1 : 0) +
    (location !== FILTER_ALL ? 1 : 0) +
    (customer !== FILTER_ALL ? 1 : 0) +
    (progressFilter !== "all" ? 1 : 0) +
    (query.trim() ? 1 : 0);

  function resetFilters() {
    setQuery("");
    setCategory(FILTER_ALL);
    setLocation(FILTER_ALL);
    setCustomer(FILTER_ALL);
    setProgressFilter("all");
  }

  /* ───────────── Verträge ───────────── */

  const campaignCountByContract = useMemo(() => {
    const map = new Map<number, number>();
    for (const v of views) {
      const id = Number(v.contract.id);
      map.set(id, (map.get(id) ?? 0) + 1);
    }
    return map;
  }, [views]);

  function openWizard(contract?: MaintenanceContract | null) {
    setWizardContract(contract ?? null);
    setWizardOpen(true);
  }

  const formalContracts = useMemo(() => contracts.filter((c) => !isOnCallContract(c)), [contracts]);
  const onCallContracts = useMemo(() => contracts.filter((c) => isOnCallContract(c)), [contracts]);

  const hasContracts = contracts.length > 0;
  const hasCampaigns = views.length > 0;

  return (
    <AppShell
      title="Wartungen"
      subtitle="Wartungen planen und durchführen, Verträge und Arbeit auf Zuruf verwalten."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            data-tour="wartung-neu"
            onClick={() => openWizard()}
            leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
            disabled={loading || !hasContracts}
            title={!loading && !hasContracts ? "Bitte zuerst einen Vertrag oder einen Eintrag auf Zuruf anlegen." : undefined}
          >
            Neue Wartung
          </Button>
          <Link
            to="/wartungen/vertrag-form"
            data-tour="wartung-vertrag"
            className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
          >
            <DocumentPlusIcon className="h-4 w-4" aria-hidden />
            Neuer Vertrag
          </Link>
          <Link
            to="/wartungen/objekt-uebersicht"
            title="Alle Wartungsobjekte je Ort durchsuchen"
            className="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label"
          >
            <CubeIcon className="h-4 w-4" aria-hidden />
            Objekte
          </Link>
          <Link
            to="/wartungen/einstellungen"
            title="Wartungskategorien und Formulare verwalten"
            className="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label"
          >
            <Cog6ToothIcon className="h-4 w-4" aria-hidden />
            Einstellungen
          </Link>
        </div>
      }
    >
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl<Tab>
          name="Bereich"
          size="md"
          value={tab}
          onChange={setTab}
          options={[
            {
              value: "wartungen",
              label: (
                <span className="inline-flex items-center gap-1.5">
                  Wartungen
                  {!loading ? <span className="eh-segmented-item__badge">{views.length}</span> : null}
                </span>
              ),
            },
            {
              value: "vertraege",
              label: (
                <span className="inline-flex items-center gap-1.5">
                  Verträge
                  {!loading ? <span className="eh-segmented-item__badge">{formalContracts.length}</span> : null}
                </span>
              ),
            },
            {
              value: "auf-zuruf",
              label: (
                <span className="inline-flex items-center gap-1.5">
                  Auf Zuruf
                  {!loading ? <span className="eh-segmented-item__badge">{onCallContracts.length}</span> : null}
                </span>
              ),
            },
          ]}
        />
        {loading ? (
          <span className="flex items-center gap-2 text-sm text-label-secondary" aria-live="polite">
            <Spinner /> Lade Wartungen …
          </span>
        ) : null}
      </div>

      {!loading && (!hasContracts || (tab === "wartungen" && !hasCampaigns)) ? (
        <OnboardingSteps
          hasContracts={hasContracts}
          onCreateContract={() => navigate("/wartungen/vertrag-form")}
          onCreateMaintenance={() => openWizard()}
        />
      ) : null}

      {tab === "wartungen" && !loading && hasCampaigns ? (
        <>
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              label={year === FILTER_ALL ? "Wartungen gesamt" : `Wartungen ${year}`}
              value={kpis.total}
              hint="Alle Wartungen im gewählten Jahr"
              active={progressFilter === "all"}
              onClick={() => setProgressFilter("all")}
            />
            <KpiTile
              label="Offen"
              value={kpis.open}
              hint="Noch kein Objekt gewartet"
              tone="neutral"
              active={progressFilter === "open"}
              onClick={() => setProgressFilter(progressFilter === "open" ? "all" : "open")}
            />
            <KpiTile
              label="In Bearbeitung"
              value={kpis.in_progress}
              hint="Teilweise gewartet"
              tone="orange"
              active={progressFilter === "in_progress"}
              onClick={() => setProgressFilter(progressFilter === "in_progress" ? "all" : "in_progress")}
            />
            <KpiTile
              label="Abgeschlossen"
              value={kpis.done}
              hint="Alle Objekte gewartet"
              tone="green"
              active={progressFilter === "done"}
              onClick={() => setProgressFilter(progressFilter === "done" ? "all" : "done")}
            />
          </section>

          <section className="space-y-3 rounded-ios border border-separator bg-card p-4 shadow-ios">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[14rem] flex-1">
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
                  aria-hidden
                />
                <input
                  type="search"
                  className="eh-input pl-9"
                  placeholder="Suchen: Kunde, Ort, Kategorie, Vertrag …"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Wartungen durchsuchen"
                />
              </div>
              <ChipRow label="Jahr" options={yearOptions} selected={year} onSelect={setYear} />
              <button
                type="button"
                className={clsx(
                  "inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition",
                  showMoreFilters || activeFilterCount > 0
                    ? "bg-accent/10 text-accent"
                    : "text-label-secondary hover:bg-fill-tertiary hover:text-label",
                )}
                onClick={() => setShowMoreFilters((v) => !v)}
                aria-expanded={showMoreFilters}
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden />
                Filter
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-accent px-1.5 text-xs font-semibold text-label-on-accent">{activeFilterCount}</span>
                ) : null}
              </button>
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-sm text-label-secondary hover:text-label"
                  onClick={resetFilters}
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden />
                  Zurücksetzen
                </button>
              ) : null}
            </div>

            {showMoreFilters ? (
              <div className="grid gap-3 border-t border-separator pt-3 md:grid-cols-[1fr_auto_auto]">
                {categoryOptions.length > 1 ? (
                  <ChipRow label="Kategorie" options={categoryOptions} selected={category} onSelect={setCategory} />
                ) : (
                  <span />
                )}
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Ort</span>
                  <NativeSelect selectSize="sm" value={location} onChange={(e) => setLocation(e.target.value)}>
                    <option value={FILTER_ALL}>Alle Orte</option>
                    {locationOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Kunde</span>
                  <NativeSelect selectSize="sm" value={customer} onChange={(e) => setCustomer(e.target.value)}>
                    <option value={FILTER_ALL}>Alle Kunden</option>
                    {customerOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>
                </label>
              </div>
            ) : null}
          </section>

          <p className="text-sm text-label-secondary" aria-live="polite">
            {filtered.length === yearScoped.length
              ? `${filtered.length} Wartung${filtered.length === 1 ? "" : "en"}${year !== FILTER_ALL ? ` in ${year}` : ""}`
              : `${filtered.length} von ${yearScoped.length} Wartungen${year !== FILTER_ALL ? ` in ${year}` : ""}`}
            {grouped.length > 1 ? ` · ${grouped.length} Kunden` : ""}
          </p>

          {filtered.length === 0 ? (
            <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center shadow-ios">
              <p className="text-sm font-medium text-label">Keine Wartungen für diese Auswahl.</p>
              <p className="mt-1 text-sm text-label-secondary">
                {yearScoped.length === 0
                  ? `Für ${year} sind noch keine Wartungen angelegt.`
                  : "Passe Suche oder Filter an, oder setze die Filter zurück."}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {activeFilterCount > 0 ? (
                  <Button type="button" variant="secondary" onClick={resetFilters}>
                    Filter zurücksetzen
                  </Button>
                ) : null}
                {yearScoped.length === 0 && year !== FILTER_ALL ? (
                  <Button type="button" variant="secondary" onClick={() => setYear(FILTER_ALL)}>
                    Alle Jahre anzeigen
                  </Button>
                ) : null}
                <Button type="button" onClick={() => openWizard()} leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}>
                  Neue Wartung
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map((group) => (
                <section key={group.label} aria-label={`Wartungen von ${group.label}`}>
                  <div className="mb-2 flex items-baseline gap-2">
                    <h2 className="text-sm font-semibold text-label">{group.label}</h2>
                    <span className="text-xs text-label-tertiary">
                      {group.items.length} Wartung{group.items.length === 1 ? "" : "en"}
                    </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {group.items.map((view) => (
                      <MaintenanceCampaignCard
                        key={view.id}
                        view={view}
                        showCustomer={false}
                        averagePerAsset={
                          view.campaignId != null ? averageByCampaign[view.campaignId] : null
                        }
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      ) : null}

      {tab === "vertraege" && !loading && hasContracts ? (
        <VertraegeTab
          contracts={formalContracts}
          campaignCountByContract={campaignCountByContract}
          onCreateMaintenance={(contract) => openWizard(contract)}
        />
      ) : null}

      {tab === "auf-zuruf" && !loading && hasContracts ? (
        <VertraegeTab
          variant="on_call"
          contracts={onCallContracts}
          campaignCountByContract={campaignCountByContract}
          onCreateMaintenance={(contract) => openWizard(contract)}
        />
      ) : null}

      {showLegacyAccess ? (
        <details className="rounded-ios border border-separator bg-card shadow-ios">
          <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-label-secondary hover:text-label">
            Brandschutztürwartung (Legacy) &amp; Offpaper
          </summary>
          <div className="border-t border-separator px-4 py-3">
            <p className="text-sm text-label-secondary">
              Wartungsprotokolle erzeugen und ablegen – sowie Offpaper-Formulare und Legacy-Einstellungen.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/wartungen/brandschutztuerwartung"
                className="inline-flex h-9 items-center rounded-lg bg-card px-3.5 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
              >
                Wartungsprotokolle
              </Link>
              <Link
                to="/offpaper"
                className="inline-flex h-9 items-center rounded-lg bg-card px-3.5 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
              >
                Offpaper Formulare
              </Link>
            </div>
          </div>
        </details>
      ) : null}

      <NeueWartungDialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialContract={wizardContract}
        contracts={contracts}
      />
    </AppShell>
  );
}
